import { readVersionFromEtag, sendOutsideSaveAttempt } from './saveHttp';

/**
 * Hand-written client for the connector-next writes on a patient: intake,
 * tags and assignment. Every write is guarded by the patient's version and
 * returns its next one. None is part of a dataset save, so none carries a
 * save attempt id.
 */

/** A patient's intake as the connector takes it; null means nobody has chosen it. */
export type IntakeBody = {
    siteId: string | null;
    radiationOncologistId: string | null;
    plannerId: string | null;
    /** "yyyy-MM-dd". */
    treatmentStart: string | null;
};

/** Sends `body` to the patient's `action` route, guarded by If-Match, and returns the next version. */
export async function sendPatientAction(
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    patientId: string,
    action: string,
    ifMatch: string,
    body?: unknown
): Promise<string> {
    const response = await sendOutsideSaveAttempt(
        `/api/patients/${patientId}/${action}`,
        body === undefined
            ? { method }
            : {
                  method,
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(body),
              },
        ifMatch
    );
    return readVersionFromEtag(response);
}

/** Replaces the patient's site, radiation oncologist, planner and treatment start. */
export function postIntake(
    patientId: string,
    intake: IntakeBody,
    ifMatch: string
): Promise<string> {
    return sendPatientAction('POST', patientId, 'intake', ifMatch, intake);
}

/** Replaces the patient's clinical tags with the tags given, by id. */
export function postTags(
    patientId: string,
    tagIds: readonly string[],
    ifMatch: string
): Promise<string> {
    return sendPatientAction('POST', patientId, 'tags', ifMatch, { tagIds });
}

/** Clears the caller's own assignment of the patient. */
export function postRelease(
    patientId: string,
    ifMatch: string
): Promise<string> {
    return sendPatientAction('POST', patientId, 'release', ifMatch);
}

/**
 * Assigns the patient to the user `userId`, or to nobody when it is null,
 * whoever holds it now. For a user permitted to assign patients.
 */
export function putAssignment(
    patientId: string,
    userId: string | null,
    ifMatch: string
): Promise<string> {
    return sendPatientAction('PUT', patientId, 'assignment', ifMatch, {
        assignedToUserId: userId,
    });
}
